import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { ProfileService, PublicProfile } from '../../../services/profile.service';
import { UserService } from '../../../services/user.service';
import { StudentNav } from '../student-nav/student-nav';
import { CounselorNav } from '../../views/counselor-view/counselor-nav/counselor-nav';
import { UserAvatar } from '../user-avatar/user-avatar';
import { CAREERS } from '../profile-setup-dialog/profile-setup.constants';

const GRADE_LABELS: Record<number, string> = {
  6: '6th Grade',
  7: '7th Grade',
  8: '8th Grade',
  9: 'Freshman',
  10: 'Sophomore',
  11: 'Junior',
  12: 'Senior',
};

@Component({
  selector: 'app-user-profile-view',
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    StudentNav,
    CounselorNav,
    UserAvatar,
  ],
  templateUrl: './user-profile-view.html',
  styleUrls: ['../../views/student-view/profile-page/my-profile.css', './user-profile-view.css'],
})
export class UserProfileView implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private location = inject(Location);
  private title = inject(Title);
  private destroyRef = inject(DestroyRef);
  private profileService = inject(ProfileService);
  private userService = inject(UserService);

  /** The viewed user's profile, or null while loading / when not found. */
  profile = signal<PublicProfile | null>(null);
  loading = signal(true);
  notFound = signal(false);
  loadError = signal(false);

  /** The viewer's user type, used to pick the navigation shell. */
  viewerType = signal<string | null>(null);

  isFacultyViewer = computed(() => this.viewerType() === 'faculty');

  careerLabel = computed(() => {
    const id = this.profile()?.careerId;
    return id ? (CAREERS.find((c) => c.id === id)?.label ?? '') : '';
  });

  gradeLabel = computed(() => {
    const grade = this.profile()?.gradeLevel;
    return grade ? (GRADE_LABELS[grade] ?? `Grade ${grade}`) : '';
  });

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.load(Number(params.get('userId')));
    });
  }

  private load(userId: number): void {
    this.profile.set(null);
    this.notFound.set(false);
    this.loadError.set(false);
    this.loading.set(true);

    if (!Number.isInteger(userId) || userId <= 0) {
      this.notFound.set(true);
      this.loading.set(false);
      return;
    }

    this.userService.getCurrentUser().subscribe({
      next: (viewer) => {
        this.viewerType.set(viewer?.userType ?? null);

        // Your own profile has an editable page; send the user there.
        if (viewer?.id === userId) {
          this.router.navigateByUrl('/profilePage', { replaceUrl: true });
          return;
        }

        this.profileService.getPublicProfile(userId).subscribe({
          next: (profile) => {
            this.profile.set(profile);
            this.notFound.set(!profile);
            this.loading.set(false);
            this.title.setTitle(`${profile?.displayName || 'Profile'} | Handle`);
          },
          error: () => {
            this.loadError.set(true);
            this.loading.set(false);
          },
        });
      },
      error: () => {
        this.loadError.set(true);
        this.loading.set(false);
      },
    });
  }

  goBack(): void {
    this.location.back();
  }
}
